// src/pages/WorkPage.js
import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import ProjectModal from '../components/ProjectModal';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Checkbox,
  TableFooter,
  Menu,
  MenuItem,
  LinearProgress,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'; // 드래그 앤 드롭 라이브러리
import { AuthContext } from '../components/util/AuthContext';
import * as XLSX from 'xlsx'; // 엑셀 파일 생성 라이브러리
import { useLocation, useNavigate } from 'react-router-dom';
import AuthorSelector from '../components/AuthorSelector';
import { useQueryParam } from '../components/util/useQueryParam';

/** ---------- styles ---------- */
const cellStyle = {
  paddingTop: '12px',
  paddingBottom: '12px',
  paddingLeft: '10px',
  paddingRight: '10px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 0, // ← 추가: 셀 너비가 colgroup을 넘어 확장되지 않도록
};

/** ---------- utils: base64 url-safe 디코더 ---------- */
function normalizeBase64Url(b64url){
  let s = b64url.replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4) s += "="; // padding 복원
  return s;
}
// base64 디코딩 함수
function decodeDefaults(param){
  const b64 = /[-_]/.test(param) ? normalizeBase64Url(param) : param;
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const jsonText = new TextDecoder('utf-8').decode(bytes)
  return JSON.parse(jsonText);
}

/** ---------- utils: 엑셀 변환 ---------- */
const toExcelRows = (rows = []) =>
  rows.map((p) => ({
    등록일: p.uploadDate ? new Date(p.uploadDate).toISOString().split('T')[0] : '',
    현장명: p.siteName ?? '',
    담당자: p.manager ?? '',
    공사명: p.constructionDetails ?? '',
    공사현황: p.constructionStatus ?? '',
    비고: p.remarks ?? '',
    견적금액: p.estimateAmount ?? 0,
    자재비: p.materialCost ?? 0,
    외주비: p.outsourcingCost ?? 0,
    외주업체: p.outsourcingCompany ?? '',
    착공예정일: p.startDate ?? '',
    준공일: p.completeDate ?? '',
}));

const WorkPage = () => {
  const [projects, setProjects] = useState([]);   // 공사 리스트
  const [totalElements, setTotalElements] = useState(0);  //총 데이터리스트 개수(페이징)

  const [openModal, setOpenModal] = useState(false);    // 모달창 온오프 상태
  const [modalDefaultValues, setModalDefaultValues] = useState(null);   // 모달창에 전달할 값

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);   // 브라우저 창 높이 상태
  const [anchorEl, setAnchorEl] = useState(null); // 드롭다운 상태 관리
  const [loading, setLoading] = useState(true); // 로딩 상태 추가

  const [selectedProjects, setSelectedProjects] = useState([]);// 체크된 프로젝트 id 관리

  const [searchText, setSearchText] = useState('');

  const [pageParam, setPageParam] = useQueryParam('page', '0');
  const [sizeParam, setSizeParam] = useQueryParam('size', '100');

  const page = Number(pageParam ?? 0);
  const rowsPerPage = Number(sizeParam ?? 100);

  const [author, setAuthor] = useState(null);
  const [authorParam, setAuthorParam] = useQueryParam('author', null);
  const authorUsername = authorParam ?? null;

  // 추후 사용할 수 있는 필터 현재X
  const [statusFilter, setStatusFilter] = useState([]); // 공사현황 필터 상태
  const [statusAnchorEl, setStatusAnchorEl] = useState(null); // 공사현황 필터 드롭다운 상태 관리

  const [xref, setXref] = useState(null); // 인트라 url 상태
  const savingRef = useRef(false); // 저장 상태 관리
  
  const { user } = useContext(AuthContext); // 유저 정보를 담고있는 AuthContext

  const location = useLocation(); // 현재 경로 정보
  const navigate = useNavigate(); // 페이지 이동 함수

  const currentYear = new Date().getFullYear();
  // [추가] 선택된 연도 상태 (기본값: '' -> 전체)
  const [selectedYear, setSelectedYear] = useState('');
  

  /** ---------- 계산값 메모이제이션 ---------- */
  const tableHeight = useMemo(() => windowHeight - 210, [windowHeight]);

  const selectedProjectsData = useMemo(
    () => projects.filter((p) => selectedProjects.includes(p.id)),
    [projects, selectedProjects]
  );

  const sumEstimate = useMemo(
    () => selectedProjectsData.reduce((acc, p) => acc + (p.estimateAmount || 0), 0),
    [selectedProjectsData]
  );
  const sumMaterial = useMemo(
    () => selectedProjectsData.reduce((acc, p) => acc + (p.materialCost || 0), 0),
    [selectedProjectsData]
  );
  const sumOutsourcing = useMemo(
    () => selectedProjectsData.reduce((acc, p) => acc + (p.outsourcingCost || 0), 0),
    [selectedProjectsData]
  );

 
  /** ---------- 포맷터 ---------- */
  const formatNumber = useCallback((n) => {
    if (typeof n === 'number') return new Intl.NumberFormat('ko-KR').format(n);
    return n ?? '';
  }, []);


  /** ---------- 공사 리스트 가져오는 함수 ---------- */
  const fetchProjects = useCallback(async () => {
    try{
      setLoading(true);
      // 백엔드 엔드포인트는 Pageable을 받아서 JSON 형태의 Page 객체를 반환
      const params = new URLSearchParams({
        page : page,
        size : rowsPerPage,
      });
      if (searchText?.trim()) params.set('search', searchText);
      if (authorUsername) params.set('author', authorUsername);
      // [추가] 연도가 선택되어 있다면 파라미터에 추가 (예: year=2026)
      if (selectedYear) params.set('year', selectedYear);

      const response = await fetch(`/api/projects?${params.toString()}`);
      if (!response.ok) {
        throw new Error('데이터를 가져오지 못했습니다.');
      }
      const data = await response.json();

      // Spring Data JPA의 Page 객체는 일반적으로 content와 totalElements 등을 포함
      setProjects(data.content || []);
      setTotalElements(data.totalElements || 0);
      // 페이지가 변경될 때 체크 정보 초기화
      setSelectedProjects([]);
    }catch(error){
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchText, authorUsername, selectedYear]);

  //컴포넌트가 마운트되면 공사 리스트 가져옴
  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight); // 브라우저 크기 저장
    window.addEventListener('resize', handleResize);                // 이벤트리스너 추가
    fetchProjects();
    return () => window.removeEventListener('resize', handleResize);// 이벤트리스너 삭제
  }, [fetchProjects]);


  /** ---------- 인트라에서 왔을 때 컴포넌트 마운트 시 모달 파라미터 처리 ---------- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modal = params.get('modal'); // 모달 파라미터 가져오기
    const projectId = params.get('projectId'); // 프로젝트 id 가져오기

    if(projectId && projectId !== null && projectId !== 'null' && projectId !== 'undefined') {
      handleProjectClick(projectId);
    }

    if(modal === "project") {
      

      //기본값 전달이 필요하면 ?defaults=BASE64_JSON 형식으로 받아서 복원
      const defaults = params.get('defaults');
      if (defaults) {
        try {
          const json = decodeDefaults(defaults);
          console.log(json);
          setModalDefaultValues({
            siteName : json.siteName ?? '',
            manager : json.manager ?? '',
            constructionStatus : json.constructionStatus ?? '',
            startDate : json.startDate ?? '',
            endDate : json.endDate ?? '',
          });
        } catch (e) {
          console.warn('defaults 파싱 실패', e);
        }
      }

      setXref(params.get('xref') || null);
      setOpenModal(true);
      
      params.delete('modal');
      params.delete('defaults');
      params.delete('xref');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });

    }
  }, [location.search, location.pathname, navigate]);

  /** ---------- 드롭다운 ---------- */
  const handleClickMenu = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleCloseMenu = useCallback(() => setAnchorEl(null), []);

  /** ---------- 페이지네이션/검색 ---------- */
  //페이지 이동
  const handleChangePage = useCallback((_event, newPage) => {setPageParam(String(newPage));}, [setPageParam]);
  //리스트 개수 변경
  const handleChangeRowsPerPage = useCallback((event) => {
    const newSize = parseInt(event.target.value, 10);
    setSizeParam(String(newSize));
    setPageParam('0');  // 사이즈 변경 시 첫 페이지로 이동
  }, [setSizeParam, setPageParam]);
  // 검색어 핸들러
  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value ?? ''); // 항상 문자열
    setPageParam('0'); // 검색 시 첫 페이지로 이동
  }, [setPageParam]);
  // 작성자 필터 핸들러
  const handleAuthorChange = useCallback((user) => {
    setAuthor(user);
    setAuthorParam(user?.username ?? null);
    setPageParam('0');
  }, [setAuthorParam, setPageParam]);
  // 내가 작성한 게시물만 보기
  const handleMyPostsToggle = useCallback((e) => {
    const on = e.target.checked;
    if(on){
      setAuthor({ username: user?.username, userNick: user?.userNick });
      setAuthorParam(user?.username ?? null);
    }else{
      setAuthor(null);
      setAuthorParam(null);
    }
    setPageParam('0');
  }, [user, setAuthorParam, setPageParam]);



  /** ---------- 날짜 필터 변경 ---------- */
  // [추가] 연도 변경 핸들러
  const handleYearChange = useCallback((e) => {
    setSelectedYear(e.target.value);
    setPageParam('0'); // 필터 변경 시 1페이지로 이동
  }, [setPageParam]);

  // [추가] 연도 목록 생성 (2020년부터 현재 연도까지)
  const yearOptions = Array.from({ length: currentYear - 2019 }, (_, i) => 2020 + i);
  yearOptions.push("일자미지정");


  
  /** ---------- 체크박스 ---------- */
  // 체크박스 모두 선택/해제 핸들러
  const handleSelectAllChange = useCallback((event) => {
    if (event.target.checked) setSelectedProjects(projects.map(project => project.id));
    else  setSelectedProjects([]);
  }, [projects]);

  // 개별 체크박스 핸들러(행 클릭과 모달 오픈 이벤트 방지 위해 event.stopPropagation() 적용)
  const handleCheckboxChange = useCallback((event, id) => {
    event.stopPropagation();
    setSelectedProjects((prev) => (event.target.checked ? [...prev, id] : prev.filter((pid) => pid !== id)));
  }, []);


  /** ---------- 엑셀 다운로드 (선택된 항목만) ---------- */
  const handleExcelDownload = useCallback(() => {
    const selectedData = projects.filter(project => selectedProjects.includes(project.id));
    if (selectedData.length === 0) {
      window.alert('엑셀로 다운로드할 항목을 먼저 선택해주세요.');
      return;
    }
    // 워크시트 생성
    const ws = XLSX.utils.json_to_sheet(toExcelRows(selectedData));
    // 워크북에 시트 추가 후 파일 다운로드
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projects");
    XLSX.writeFile(wb, "selected_projects.xlsx");
  }, [projects, selectedProjects]);


  /** ---------- 드래그 앤 드롭 ---------- */
  // 드래그가 끝났을 때 실행되는 함수
  const onDragEnd = useCallback(async (result) => {
    if (!result.destination) return;

    const reorderedProjects = Array.from(projects);
    const [draggedItem] = reorderedProjects.splice(result.source.index, 1);
    reorderedProjects.splice(result.destination.index, 0, draggedItem);

    setProjects(reorderedProjects);

    const draggedItemId = draggedItem.id;
    const targetItemId = reorderedProjects[result.destination.index + (result.destination.index < result.source.index ? 1 : -1)]?.id;
    const isAbove = result.destination.index < result.source.index;

    try {
      await fetch('/api/projects/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draggedItemId, targetItemId, isAbove })
      });
    } catch (error) {
      console.error('순서 변경 실패:', error);
    }
  }, [projects]);


  /** ---------- 신규 등록(빈 데이터로 모달 열기) ---------- */
  const handleOpenModalForNew = useCallback(() => {
    setModalDefaultValues(null);
    setOpenModal(true);
  }, []);
  

  /** ---------- 상세 보기(모달 열기) ---------- */
  // 리스트 항목 클릭 시, 해당 프로젝트의 상세 데이터를 받아서 폼에 채워주고 모달을 열기
  const handleProjectClick = useCallback(async (projectId) => {
    try {
      const response = await fetch(`/api/projects/${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch project detail');
      const data = await response.json();
      setModalDefaultValues({
        id : data.id,
        siteName: data.siteName,
        manager: data.manager,
        constructionDetails: data.constructionDetails,
        constructionStatus: data.constructionStatus,
        constructionPre : data.constructionPre || '공사준비',
        remarks: data.remarks,
        estimateAmount: data.estimateAmount || 0,
        materialCost: data.materialCost || 0,
        outsourcingCost: data.outsourcingCost || 0,
        outsourcingCompany: data.outsourcingCompany || '',
        // difference: data.difference || 0,
        startDate: data.startDate,
        completeDate: data.completeDate || '',
        ipList: data.detail?.ipList || '',
        accountInfo: data.detail?.accountInfo || '',
        drawings: data.detail?.drawings || [], 
        photos: data.detail?.photos || [],
        contractFiles: data.detail?.contractFiles || [],
        latestUpdate: data.detail?.latestUpdate || '',
        xref: data.xref || ''
      });
      setOpenModal(true);
      console.log('Fetched project detail:', data);
    } catch (error) {
      console.error('Error fetching project detail:', error);
    }
  }, []);

  
  /** ---------- 저장 ---------- */
  // 모달에서 폼 제출 시 호출되는 함수
  const handleSaveProject = useCallback(
    async (formData) => {
      if(savingRef.current){
        // 저장중이면 아무것도 하지않음(중복저장 호출 차단)
        return Promise.resolve();
      }
      savingRef.current = true;

      const formDataToSend = new FormData();
      // 텍스트 필드들을 하나의 객체로 묶기
      const projectObj = {
        siteName: formData.siteName,
        manager: formData.manager,
        constructionDetails: formData.constructionDetails,
        constructionStatus: formData.constructionStatus,
        constructionPre : formData.constructionPre,
        remarks: formData.remarks,
        estimateAmount: formData.estimateAmount,
        materialCost: formData.materialCost,
        outsourcingCost: formData.outsourcingCost,
        outsourcingCompany: formData.outsourcingCompany,
        // difference: formData.difference,
        startDate: formData.startDate,
        completeDate: formData.completeDate,
        xref: xref || formData.xref,
        detail: {
          ipList: formData.ipList || "",
          accountInfo: formData.accountInfo || ""
        },
        // percentage: formData.percentage,
        // 삭제할 파일 목록도 포함
        drawingsToDelete: formData.drawingsToDelete || [],
        photosToDelete: formData.photosToDelete || [],
        contractFilesToDelete: formData.contractFilesToDelete || [],
        createdByNick: user.userNick, // 폼 제출시에만 사용할 유저 이름 및 네임
        createdByName: user.username,
      };
      
      // 현재 시간을 "yy-mm-dd hh:mm (사용자이름) |" 형식으로 포맷하는 헬퍼 함수
      const formatDate = (date) => {
        const yy = String(date.getFullYear()).slice(-2);
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${yy}-${mm}-${dd} ${hh}:${min} (${user.userNick}) | `;
      };

      const currentDateTime = new Date();
      const formattedTime = formatDate(currentDateTime);

      // 업데이트 모드인 경우, modalDefaultValues에 id가 존재한다면 id 추가하고 기존 latestUpdate에 추가기록
      // 신규등록인 경우 업로드 날자 추가 uploadDate에 yy:mm:dd 저장 latestUpdate에 yy:mm:dd hh:mm 저장
      const isUpdate = modalDefaultValues && modalDefaultValues.id;
      if (isUpdate) {
        projectObj.id = modalDefaultValues.id;
        // 기존 최신 업데이트 시간 기록 변수
        const prevLatestUpdate = modalDefaultValues?.latestUpdate || "";
        projectObj.detail.latestUpdate = prevLatestUpdate + formattedTime;
      }else{
        projectObj.uploadDate = currentDateTime;
        projectObj.detail.latestUpdate = formattedTime;
      }

      

      // project 객체를 JSON 문자열로 변환 후 Blob으로 감싸 "project" 파트에 추가
      formDataToSend.append(
        'project',
        new Blob([JSON.stringify(projectObj)], { type: 'application/json' })
      );

      // 파일들 추가
      if (formData.drawings) {
        formData.drawings.forEach(file => formDataToSend.append('drawings', file));
      }
      if (formData.photos) {
        formData.photos.forEach(file => formDataToSend.append('photos', file));
      }
      if (formData.contractFiles) {
        formData.contractFiles.forEach(file => formDataToSend.append('contractFiles', file));
      }

      // 삭제할 파일 목록 추가 (JSON 문자열 형태로)
      if (formData.drawingsToDelete && formData.drawingsToDelete.length > 0) {
        formDataToSend.append('drawingsToDelete', JSON.stringify(formData.drawingsToDelete));
      }
      if (formData.photosToDelete && formData.photosToDelete.length > 0) {
        formDataToSend.append('photosToDelete', JSON.stringify(formData.photosToDelete));
      }
      if (formData.contractFilesToDelete && formData.contractFilesToDelete.length > 0) {
        formDataToSend.append('contractFilesToDelete', JSON.stringify(formData.contractFilesToDelete));
      }


      try {
          // 신규 등록이면 '/api/projects', 업데이트면 '/api/projects/update' URL 사용
        // 업데이트의 경우, HTTP 메서드를 PUT (혹은 API에 맞춰 POST)로 지정
        const url = isUpdate ? '/api/projects/update' : '/api/projects';
        const method = isUpdate ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method,
          body: formDataToSend,
        });

        if (!response.ok) {
          throw new Error(`프로젝트 ${isUpdate ? '업데이트' : '등록'} 실패`);
        }
        const savedProject = await response.json();

        // 서버에서 반환받은 프로젝트 데이터를 기반으로 상태 업데이트: 신규 등록이면 추가, 업데이트면 기존 항목 교체
        if (isUpdate) {
          setProjects(prev =>
            prev.map(project => (project.id === savedProject.id ? savedProject : project))
          );
        } else {
          await fetchProjects();
        }
        setOpenModal(false);
        window.confirm('저장되었습니다.');
        return savedProject; // Modal 쪽에서 저장 결과를 받을 수 있도록 반환
      } catch (error) {
        console.error('Error saving project:', error);
        throw error; // Modal 쪽에서 오류 처리를 할 수 있도록 오류 전파
      } finally {
        savingRef.current = false; // 저장 완료 후 저장 중 상태 초기화
      }
  }, [fetchProjects, modalDefaultValues, user?.userNick, xref]);

  return (
    <div style={{backgroundColor:'white'}}>
      {/* 같은 줄에 엑셀 다운로드 버튼, 중앙 공간, 공사 등록 버튼 배치 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingLeft: '10px', paddingRight: '10px', paddingTop: '10px' }}>
        {/* 엑셀 다운로드 버튼 */}
        <Button
          color="primary"
          startIcon={<FileDownloadIcon />}
          onClick={handleClickMenu} // 버튼 클릭 시 드롭다운 열기
        >
          엑셀로 받기
        </Button>
        {/* 드롭다운 메뉴 */}
        <Menu
          anchorEl={anchorEl} // 메뉴의 앵커 요소
          open={Boolean(anchorEl)} // 앵커 요소가 존재하면 메뉴 열림
          onClose={handleCloseMenu} // 메뉴 닫기
        >
          <MenuItem onClick={() => { 
            handleExcelDownload();
            handleCloseMenu();
          }}>
            <FileDownloadOutlinedIcon />
            선택 항목 다운
          </MenuItem>
          <MenuItem onClick={() => {
            handleCloseMenu();
            // "전체 항목 다운" 클릭 시 동작 추가
          }}>
            <FileDownloadOutlinedIcon />
            전체 항목 다운
          </MenuItem>
        </Menu>

        {/* [추가/수정] 중앙: 연도 선택 드롭다운 */}
        {/* flex: 1을 주어 남은 공간을 차지하게 하고, 내부 내용을 중앙 정렬 */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <InputLabel id="year-select-label">착공년도</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="착공년도"
            >
              <MenuItem value="">
                <em>전체</em>
              </MenuItem>
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year === "일자미지정" ? "일자미지정" : year + "년"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* 공사 등록 버튼 */}
        <Button color="primary" startIcon={<AddIcon />} onClick={handleOpenModalForNew}>
          공사 등록
        </Button>
      </div>
      <Paper>
        <Box sx = {{ display : 'flex', alignItems : 'center', justifyContent : 'space-between', padding : '0px 10px 0px 20px' }}>
          {/* <TextField
            size="small"
            variant="standard"
            placeholder="검색"
            value={searchText}
            onChange={handleSearchChange}
            sx={{ width : 200}}
          /> */}
          <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
            <TextField
              size="small"
              variant="standard"
              placeholder="검색"
              value={searchText ?? ''}
              onChange={handleSearchChange}
            sx={{ width: 200 }}
          />
          <AuthorSelector value={author} onChange={handleAuthorChange} />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!!authorUsername && authorUsername === user?.username}
                onChange={handleMyPostsToggle}
              />
            }
            label="내 글만"
          />
          </Box>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 15, 20, 30, 50, 100]}
            labelRowsPerPage="리스트 표시 개수 :"
          />
        </Box>
        <Box
          sx={{
            width : '100%',
            overflowX : 'auto',
            margin: '0 auto',
          }}
        >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="projectsDroppable">
            {(provided) => (
              <TableContainer 
                ref={provided.innerRef}
                {...provided.droppableProps}
                component={Paper}
                sx={{ 
                  height: tableHeight,
                  width: '100%',
                  overflowX: 'auto',    // 가로 스크롤 허용
                }}
              >
                <Table arai-label="공사현황 리스트" stickyHeader aria-label="sticky table" sx={{ tableLayout: 'fixed', width: 'max-content', fontWeight: 'bold'}}>
                  {/*테이블 내부 스크롤 고정시 colgroup으로 각 열의 너비를 지정*/}
                  <colgroup>
                    <col style={{ width: '40px' }}/>{/* 체크박스 */}
                    <col style={{ width: '100px' }}/>{/* 등록일 */}
                    <col style={{ width: '190px' }}/>{/* 현장명 */}
                    <col style={{ width: '80px' }}/>{/* 담당자 */}
                    <col style={{ width: '280px' }}/>{/* 공사명 */}
                    <col style={{ width: '280px' }}/>{/* 공사현황 */}
                    <col style={{ width: '210px' }}/>{/* 비고 */}
                    <col style={{ width: '110px' }}/>{/* 견적금액 */}
                    <col style={{ width: '110px' }}/>{/* 자재비 */}
                    <col style={{ width: '110px' }}/>{/* 외주비 */}
                    <col style={{ width: '150px' }}/>{/* 외주업체 */}
                    <col style={{ width: '100px' }}/>{/* 착공예정일 */}
                    <col style={{ width: '100px' }}/>{/* 준공일 */}
                  </colgroup>
                  <TableHead
                    sx={{
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #ccc'
                      
                    }}
                  >
                    <TableRow>
                      <TableCell padding = "checkbox">
                      <Checkbox
                          indeterminate={selectedProjects.length > 0 && selectedProjects.length < projects.length}
                          checked={projects.length > 0 && selectedProjects.length === projects.length}
                          onChange={handleSelectAllChange}/>
                      </TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>등록일</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>현장명</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>담당자</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>공사명</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>공사현황</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>비고</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>견적금액</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>자재비</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>외주비</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>외주업체</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>착공예정일</TableCell>
                      <TableCell sx = {{ fontWeight: 'bold' }}>준공일</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={13} sx={{ padding: 0 }}>
                          <LinearProgress />
                        </TableCell>
                      </TableRow>
                    ) : (
                      projects.map((project, index) => (
                        <Draggable key={project.id} draggableId={project.id.toString()} index={index}>
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                backgroundColor: snapshot.isDragging ? 'rgba(0, 0, 0, 0.1)' : 'inherit',
                                boxShadow: snapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                                cursor: 'pointer',
                                backgroundColor:
                                  project.constructionPre === "공사중"
                                    ? "lightgreen"
                                    : project.constructionPre === "공사보류"
                                    ? "lightgray"
                                    : project.constructionPre === "공사완료"
                                    ? "lightblue"
                                    : "inherit"
                              }}
                              onClick={() => handleProjectClick(project.id)}>
                              <TableCell padding = "checkbox">
                                <Checkbox
                                  checked = {selectedProjects.includes(project.id)}
                                  onChange = {(event) => handleCheckboxChange(event, project.id)}
                                  onClick={(event) => event.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell style={cellStyle }>
                                {project.uploadDate ? new Date(project.uploadDate).toISOString().split('T')[0] : ''}{/* 날자만 출력 yyyy-mm-dd */}
                              </TableCell>
                              <TableCell style={cellStyle }>{project.siteName}</TableCell>
                              <TableCell style={cellStyle }>{project.manager}</TableCell>
                              <TableCell style={cellStyle }>{project.constructionDetails}</TableCell>
                              <TableCell style={cellStyle }>{project.constructionStatus}</TableCell>
                              <TableCell style={cellStyle }>{project.remarks}</TableCell>
                              <TableCell style={cellStyle }>{formatNumber(project.estimateAmount)}</TableCell>
                              <TableCell style={cellStyle }>{formatNumber(project.materialCost)}</TableCell>
                              <TableCell style={cellStyle }>{formatNumber(project.outsourcingCost)}</TableCell>
                              <TableCell style={cellStyle }>{project.outsourcingCompany}</TableCell>
                              <TableCell style={cellStyle }>{project.startDate}</TableCell>
                              <TableCell style={cellStyle }>{project.completeDate}</TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </TableBody>
                  <TableFooter sx = {{
                    position : 'sticky',
                    bottom : -1,
                    backgroundColor : 'white', // 헤더와 동일한 배경색 지정
                    zIndex : 1, // 다른 요소와 겹치지 않도록 z-index 조정
                  }}>
                    <TableRow>
                      <TableCell/>{/* 체크박스 열 */}
                      <TableCell sx = {{ fontWeight : 'bold' }}>합계</TableCell>
                      <TableCell/>
                      <TableCell/>
                      <TableCell/>
                      <TableCell/>
                      <TableCell/>
                      <TableCell sx = {{ fontWeight : 'bold' }}>{formatNumber(sumEstimate)}</TableCell>
                      <TableCell sx = {{ fontWeight : 'bold' }}>{formatNumber(sumMaterial)}</TableCell>
                      <TableCell sx = {{ fontWeight : 'bold' }}>{formatNumber(sumOutsourcing)}</TableCell>
                      <TableCell/>
                      <TableCell/>
                      <TableCell/>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}
          </Droppable>
        </DragDropContext>
        </Box>
      </Paper>
      <ProjectModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        onSubmit={handleSaveProject}
        defaultValues={modalDefaultValues}
        onProjectDeleted={(deletedId) => {
          // 삭제 완료 후 프로젝트 목록 갱신
          setProjects(prev => prev.filter(p => p.id !== deletedId));
        }}
      />
    </div>
  );
};

export default WorkPage;
